/**
 * Utilitários para formatação de dados
 */

export class FormatUtil {
  /**
   * Formata valor monetário para exibição (BRL)
   */
  static formatCurrency(value: number | string): string {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    if (isNaN(numValue)) return 'R$ 0,00';

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(numValue);
  }

  /**
   * Formata número com separadores de milhar
   */
  static formatNumber(value: number | string, decimals: number = 2): string {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    if (isNaN(numValue)) return '0';

    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(numValue);
  }

  /**
   * Formata peso (kg)
   */
  static formatWeight(value: number | string): string {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    
    if (isNaN(numValue)) return '0 kg';

    return `${this.formatNumber(numValue, 2)} kg`;
  }

  /**
   * Formata CEP (00000-000)
   */
  static formatCEP(cep: string): string {
    const cleaned = cep.replace(/\D/g, '');
    
    if (cleaned.length !== 8) return cep;

    return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
  }

  /**
   * Formata telefone brasileiro
   */
  static formatPhone(phone: string): string {
    const cleaned = phone.replace(/\D/g, '');

    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }

    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
    }

    return phone;
  }

  /**
   * Formata data (DD/MM/YYYY HH:mm)
   */
  static formatDate(date: string | Date): string {
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(dateObj);
    } catch {
      return date.toString();
    }
  }

  /**
   * Formata data simples (DD/MM/YYYY)
   */
  static formatDateSimple(date: string | Date): string {
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(dateObj);
    } catch {
      return date.toString();
    }
  }

  /**
   * Remove formatação de moeda
   */
  static parseCurrency(value: string): number {
    const cleaned = value.replace(/[^\d,]/g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  }

  /**
   * Remove formatação de número
   */
  static parseNumber(value: string): number {
    const cleaned = value.replace(/\./g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  }

  /**
   * Trunca texto com reticências
   */
  static truncate(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength)}...`;
  }

  /**
   * Capitaliza primeira letra
   */
  static capitalize(text: string): string {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  /**
   * Capitaliza todas as palavras
   */
  static capitalizeWords(text: string): string {
    if (!text) return '';
    return text
      .split(' ')
      .map((word) => this.capitalize(word))
      .join(' ');
  }

  /**
   * Formata endereço completo
   */
  static formatAddress(endereco: {
    endereco: string;
    numero?: string;
    complemento?: string;
    bairro: string;
    cidade: string;
    estado: string;
    cep?: string;
  }): string {
    const parts = [endereco.endereco];

    if (endereco.numero) {
      parts[0] += `, ${endereco.numero}`;
    }

    if (endereco.complemento) {
      parts.push(endereco.complemento);
    }

    parts.push(endereco.bairro);
    parts.push(`${endereco.cidade} - ${endereco.estado}`);

    if (endereco.cep) {
      parts.push(`CEP: ${this.formatCEP(endereco.cep)}`);
    }

    return parts.join(', ');
  }

  /**
   * Formata unidade com peso
   */
  static formatUnit(unidade: string, peso: string | number): string {
    return `${unidade} ${peso}kg`;
  }

  /**
   * Formata quantidade com unidade
   */
  static formatQuantity(quantidade: number, unidade: string): string {
    return `${quantidade} ${unidade}${quantidade > 1 ? 's' : ''}`;
  }

  /**
   * Remove caracteres especiais
   */
  static removeSpecialChars(text: string): string {
    return text.replace(/[^a-zA-Z0-9\s]/g, '');
  }

  /**
   * Limpa string para busca
   */
  static normalizeForSearch(text: string): string {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  /**
   * Formata URL de imagem
   */
  static formatImageUrl(filename: string): string {
    const baseUrl = import.meta.env.VITE_IMAGES_BASE_URL || '';
    return `${baseUrl}/${filename}`;
  }

  /**
   * Gera iniciais do nome
   */
  static getInitials(name: string): string {
    if (!name) return '';
    
    const words = name.trim().split(' ');
    
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }

    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  }

  /**
   * Formata status financeiro
   */
  static formatFinancialStatus(financas: number): string {
    return financas === 0 ? 'Ativo' : 'Com débitos';
  }
}